import type { TranslateService } from '@ngx-translate/core';
import type { AdditionalField } from 'app/store/project/addition-fields/addition-fields.reducer';
import type { CourierOutput, GoodOutput, SenderOutput, StatusOutput, StorehouseOutput } from 'core/http/project';
import type { Uuid } from 'shared/common/types';
import type { SearchBarField, SearchBarFields } from 'shared/modules/controls/search-bar/search-bar';
import type { SeparatorInputData } from 'shared/modules/controls/separator-input/separator-input.component';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { defaultAdditionalFieldNumber } from 'core/utils/additional-field-label';
import { Compilers, Formatters } from 'shared/modules/controls/search-bar/search-bar';

export function createOrderFilterForm(additional: AdditionalField[], defaultStatus?: StatusOutput): UntypedFormGroup {
  const defaultSeparator
    = (separator: SeparatorInputData['separator']): SeparatorInputData => ({ value: '', byEntry: true, separator });

  const additionalControls = additional.reduce((result, field) => {
    return { ...result, [field.name]: new UntypedFormControl(defaultSeparator(',')) };
  }, {});

  const form = new UntypedFormGroup({
    outProjectId: new UntypedFormControl(defaultSeparator(' ')),
    outOrderId: new UntypedFormControl(defaultSeparator(' ')),
    createdAt: new UntypedFormControl(),
    updatedAt: new UntypedFormControl(),
    exportedAt: new UntypedFormControl(),
    boughtOutAt: new UntypedFormControl(),
    returnedAt: new UntypedFormControl(),
    statusChangedAt: new UntypedFormControl(),
    comment: new UntypedFormControl(),
    recipient: new UntypedFormControl(defaultSeparator(',')),
    city: new UntypedFormControl(defaultSeparator(',')),
    region: new UntypedFormControl(defaultSeparator(',')),
    postcode: new UntypedFormControl(defaultSeparator(' ')),
    phone: new UntypedFormControl(defaultSeparator(' ')),
    statuses: new UntypedFormControl(),
    couriers: new UntypedFormControl(),
    senders: new UntypedFormControl(),
    track: new UntypedFormControl(defaultSeparator(' ')),
    tracking: new UntypedFormControl(defaultSeparator(',')),
    goods: new UntypedFormControl(),
    storehouse: new UntypedFormControl(),
    goodsCount: new UntypedFormControl(),
    additions: new UntypedFormGroup({
      ...additionalControls,
    }),
  });

  if (defaultStatus) {
    form.patchValue({ statuses: [defaultStatus.id] });
  }

  return form;
}

export function createOrderFields(
  additional: AdditionalField[],
  senders: SenderOutput[],
  couriers: CourierOutput[],
  statuses: StatusOutput[],
  goods: GoodOutput[],
  storehouses: StorehouseOutput[],
  translateService: TranslateService,
): SearchBarFields {
  const converter = (value: SeparatorInputData) => value.value;

  const additionalFields = additional.reduce((result, field) => {
    const number = defaultAdditionalFieldNumber(field);
    const label = number === undefined ? field.label : translateService.instant('GROUPERS.additional_field', { number });
    return {
      ...result,
      [`additions.${field.name}`]: {
        label,
        compiler: Compilers.containValues(`additional.${field.name}`),
        formatter: Formatters.justValue(converter),
      } as SearchBarField,
    };
  }, {});

  return {
    outProjectId: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.project_id'),
      compiler: Compilers.valuesFromString('lgStandingData.outProjectId'),
      formatter: Formatters.justValue(converter),
    },
    outOrderId: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.order_id'),
      compiler: Compilers.valuesFromString('lgStandingData.outOrderId'),
      formatter: Formatters.justValue(converter),
    },
    createdAt: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.created_at'),
      compiler: Compilers.dates('createdAt'),
      formatter: Formatters.dates(translateService),
    },
    updatedAt: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.updated_at'),
      compiler: Compilers.dates('updatedAt'),
      formatter: Formatters.dates(translateService),
    },
    exportedAt: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.exported_at'),
      compiler: Compilers.dates('logistic.createdAt'),
      formatter: Formatters.dates(translateService),
    },
    boughtOutAt: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.bought_out_at'),
      compiler: Compilers.dates('boughtOutAt'),
      formatter: Formatters.dates(translateService),
    },
    returnedAt: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.returned_at'),
      compiler: Compilers.dates('returnedAt'),
      formatter: Formatters.dates(translateService),
    },
    statusChangedAt: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.status_changed_at'),
      compiler: Compilers.dates('statusChangeAt'),
      formatter: Formatters.dates(translateService),
    },
    comment: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.comment'),
      compiler: Compilers.text('comment'),
    },
    recipient: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.recipient_name'),
      compiler: Compilers.containOrEqual('recipient.name'),
      formatter: Formatters.justValue(converter),
    },
    city: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.city'),
      compiler: Compilers.containOrEqual('recipient.city'),
      formatter: Formatters.justValue(converter),
    },
    region: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.region'),
      compiler: Compilers.containOrEqual('recipient.region'),
      formatter: Formatters.justValue(converter),
    },
    postcode: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.postcode'),
      compiler: Compilers.containValues('recipient.postcode'),
      formatter: Formatters.justValue(converter),
    },
    phone: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.phones'),
      compiler: Compilers.containOrEqual('recipient.phone'),
      formatter: Formatters.justValue(converter),
    },
    statuses: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.statuses'),
      compiler: Compilers.array('status', statuses.length),
      formatter: Formatters.invisible(),
    },
    couriers: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.couriers'),
      compiler: Compilers.array('logistic.courier', couriers.length),
      formatter: Formatters.array((courierId: Uuid) => {
        const found = couriers.find(c => c.id === courierId);
        return found ? found.name : translateService.instant('COMMON.unknown');
      }),
    },
    senders: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.senders'),
      compiler: Compilers.array('logistic.sender', senders.length),
      formatter: Formatters.array((senderId: Uuid) => {
        const found = senders.find(c => c.id === senderId);
        return found ? found.name : translateService.instant('COMMON.unknown');
      }),
    },
    track: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.track'),
      compiler: Compilers.valuesFromString('logistic.track'),
      formatter: Formatters.justValue(converter),
    },
    tracking: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.tracking'),
      compiler: Compilers.containOrEqual('logistic.tracking.lastName'),
      formatter: Formatters.justValue(converter),
    },
    goods: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.goods'),
      compiler: Compilers.array('cart.goods.good', goods.length),
      formatter: Formatters.array((goodId: Uuid) => {
        const found = goods.find(c => c.id === goodId);
        return found ? found.name : translateService.instant('COMMON.unknown');
      }),
    },
    storehouse: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.storehouse'),
      compiler: Compilers.array('storeHouse.id', storehouses.length),
      formatter: Formatters.array((storehouseId: Uuid) => {
        const found = storehouses.find(c => c.id === storehouseId);
        return found ? found.name : translateService.instant('COMMON.unknown');
      }),
    },
    goodsCount: {
      label: translateService.instant('QUERY_BUILDER.FIELDS.goods_count'),
      compiler: Compilers.equals('cart.totalQuantity'),
      formatter: Formatters.justValue(),
    },
    ...additionalFields,
  };
}
